
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  BookOpen,
  Target,
  Activity,
  Globe,
  Brain,
  Clock
} from 'lucide-react';
import { 
  usePlatformStats,
  useUserGrowthAnalytics,
  useContentAnalytics 
} from '@/hooks/useAnalytics';
import {
  useDisciplineAnalytics,
  usePillarAnalytics,
  useThematicTrailAnalytics,
  useCrossDisciplinaryMetrics,
  useEngagementHeatmap,
  useLearningPathAnalytics
} from '@/hooks/useDisciplineAnalytics';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

const EnhancedAnalyticsDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState('30');
  
  const { data: platformStats } = usePlatformStats();
  const { data: userGrowth } = useUserGrowthAnalytics(parseInt(timeRange));
  const { data: contentAnalytics } = useContentAnalytics(parseInt(timeRange));
  const { data: disciplineData } = useDisciplineAnalytics(parseInt(timeRange));
  const { data: pillarData } = usePillarAnalytics(parseInt(timeRange));
  const { data: trailAnalytics } = useThematicTrailAnalytics();
  const { data: crossDisciplinaryMetrics } = useCrossDisciplinaryMetrics();
  const { data: engagementHeatmap } = useEngagementHeatmap();
  const { data: learningPathData } = useLearningPathAnalytics();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Enhanced Analytics Dashboard</h2>
          <p className="text-black">Advanced insights and monitoring</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="pillars" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="pillars">Pillar Analytics</TabsTrigger>
          <TabsTrigger value="disciplines">Disciplines</TabsTrigger>
          <TabsTrigger value="trails">Thematic Trails</TabsTrigger>
          <TabsTrigger value="connections">Cross-Disciplinary</TabsTrigger>
          <TabsTrigger value="learning">Learning Paths</TabsTrigger>
        </TabsList>

        <TabsContent value="pillars" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {pillarData?.map((pillar, index) => (
              <Card key={pillar.pillar_name}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    {pillar.pillar_name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-black">{pillar.total_content}</div>
                      <div className="text-sm text-black">Total Content</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{pillar.published_content}</div>
                      <div className="text-sm text-black">Published</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-black">{pillar.total_views}</div>
                      <div className="text-sm text-black">Total Views</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-black">{pillar.average_rating}</div>
                      <div className="text-sm text-black">Avg Rating</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Engagement Score</span>
                      <span>{pillar.engagement_score}%</span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${pillar.engagement_score}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="disciplines" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Discipline Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={disciplineData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="discipline_name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total_views" fill="#8884d8" name="Views" />
                  <Bar dataKey="published_content" fill="#82ca9d" name="Published" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trails" className="space-y-4">
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Thematic Trail Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Array.isArray(trailAnalytics) && trailAnalytics.slice(0, 5).map((trail) => (
                    <div key={trail.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{trail.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">{trail.status}</Badge>
                          <span className="text-sm text-black">
                            {trail.user_count} users • {trail.avg_time_spent}min avg
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">{trail.completion_rate?.toFixed(1)}%</div>
                        <div className="text-sm text-black">Completion Rate</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="connections" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  Cross-Disciplinary Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">
                      {crossDisciplinaryMetrics?.total_connections || 0}
                    </div>
                    <div className="text-sm text-black">Total Connections</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {crossDisciplinaryMetrics?.connection_strength?.toFixed(2) || 0}
                    </div>
                    <div className="text-sm text-black">Avg Strength</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Discipline Pairs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {crossDisciplinaryMetrics?.discipline_pairs && 
                   typeof crossDisciplinaryMetrics.discipline_pairs === 'object' &&
                   crossDisciplinaryMetrics.discipline_pairs !== null &&
                   Object.entries(crossDisciplinaryMetrics.discipline_pairs as Record<string, any>)
                    .slice(0, 5)
                    .map(([pair, count]) => (
                      <div key={pair} className="flex justify-between">
                        <span className="text-sm">{pair}</span>
                        <Badge variant="outline">{String(count)}</Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="learning" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Learning Path Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">
                      {learningPathData?.total_paths || 0}
                    </div>
                    <div className="text-sm text-black">Total Paths</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {learningPathData?.completed_paths || 0}
                    </div>
                    <div className="text-sm text-black">Completed</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">
                    {learningPathData?.average_completion_time || 0} minutes
                  </div>
                  <div className="text-sm text-black">Average Completion Time</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Popular Starting Points</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {learningPathData?.most_popular_starting_points &&
                   Array.isArray(learningPathData.most_popular_starting_points) &&
                   learningPathData.most_popular_starting_points.map((point: any, index: number) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm">{point.title}</span>
                      <Badge variant="outline">{point.count} starts</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedAnalyticsDashboard;
